import React, { useEffect, useState } from 'react'
import scrollIntoView from 'scroll-into-view-if-needed'

import i18next from '@disco/i18n/i18next'
import { loadBusinessSubscription } from '@disco/state-management/actions/UserActions'
import {
  getNoSelfServePlanChangeDueTo,
  getSubscriptionPeriod,
  isEnterprisePlan,
} from '@disco/state-management/selectors/subscription'
import {
  getConstanceSettings,
  getCorePlan,
  getDSQuota,
  getDSQuotaUsed,
  getTracksLimits,
  getUser,
  getUsersLimits,
} from '@disco/state-management/selectors/user'
import captureError from '@disco/utils/captureError'
import { getWatermarkingQuota } from '@disco/utils/getWatermarkingQuota'
import { formatDate } from '@disco/utils/time-utils'

import ChargebeeScriptLoader from 'components/chargebee-form/chargebee-loader'
import SubscriptionAddOnCard from 'components/common/subscribed-user-billing/growth-add-ons/subscription-add-on-card'
import { SubscribedUserBillingCancelButton } from 'components/common/subscribed-user-billing/SubscribedUserBillingCancelButton'
import { Box, Callout, Spinner } from 'components/ds'
import ContactUsLink from 'components/ui/contact-us-link'

import useChargebeePricingPage from 'hooks/use-chargebee-pricing-page'
import { useDispatch, useSelector } from 'hooks/use-redux'

import EnterpriseCancelButton from './enterprise-cancel-button'
import YourSubscription from './index'
import PlanRow from './plan-row'
import {
  ADMIN_ONLY_MESSAGE,
  formatBillingAmount,
  getActiveAddOns,
  getAddOnsTotal,
  getBilledToLines,
  getCardDetails,
  getPlanPrice,
  hasAddOnCharge,
  PRICING_PAGE_ERROR_MESSAGE,
} from './utils'

const DISCOVERY_SUITE_LEARN_MORE_URL =
  'https://support.disco.ac/home/discovery-suite-guide'
const WATERMARKING_LEARN_MORE_URL =
  'https://support.disco.ac/home/watermarking-guide'
// The Watermarking description copies the legacy AddOns card; the duplicate goes when the CB-59 flag is cleaned up.
const WATERMARKING_DESCRIPTION = (
  <>
    Securely watermark your tracks with DISCO’s fast, secure watermarking.
    <br />
    The 1 watermarked playlist plan allows you to have one playlist watermarked
    at a time. If you’ll be watermarking multiple releases at any one time,
    choose the 3 playlist or unlimited plan. Watermarking is an add on addition
    to your base plan.
  </>
)

/**
 * Loads the business's Chargebee subscription and renders the "Your subscription" section,
 * wiring Manage subscription to the Chargebee pricing page and Cancel to the existing flow.
 */
const YourSubscriptionContainer = (): React.ReactNode => {
  const dispatch = useDispatch()
  // TODO: STELLA-31 - translate the section's copy, with its ja and ko strings, in a follow-up PR
  const locale = i18next.language
  const user = useSelector(getUser)
  const constance = useSelector(getConstanceSettings)
  const subscription = useSelector(state => state.subscription)
  const period = useSelector(getSubscriptionPeriod)
  const noPlanChangeDueTo = useSelector(getNoSelfServePlanChangeDueTo)
  // The period is only known once the subscription loads, so a pending or failed load doesn't block
  const canChangePlan =
    !noPlanChangeDueTo ||
    (!subscription.loaded && noPlanChangeDueTo === 'unsupported_period')
  const isEnterprise = useSelector(isEnterprisePlan)
  const corePlan = useSelector(getCorePlan)
  const usersLimits = useSelector(getUsersLimits)
  const tracksLimits = useSelector(getTracksLimits)
  const discoverySuiteQuota = useSelector(getDSQuota)
  const discoverySuiteUsed = useSelector(getDSQuotaUsed)
  // Selected field by field: getWatermarkingQuota builds a new object each call, which would re-render on every dispatch
  const watermarkingQuota = useSelector(
    state => getWatermarkingQuota(state).quota ?? 0,
  )
  const watermarkingUsed = useSelector(
    state => getWatermarkingQuota(state).quotaUsed,
  )
  const watermarkingUnlimited = useSelector(state =>
    Boolean(getWatermarkingQuota(state).quotaUnlimited),
  )

  const [cancelSubscriptionModalOpen, setCancelSubscriptionModalOpen] =
    useState(false)
  const [hasChargebeeScriptLoaded, setHasChargebeeScriptLoaded] =
    useState(false)
  const [hasChargebeeScriptFailed, setHasChargebeeScriptFailed] =
    useState(false)

  const isAdmin = Boolean(
    user.permissions?.includes('can_change_business_settings'),
  )
  const pricingPage = useChargebeePricingPage(
    '/settings/billing',
    isAdmin && canChangePlan,
  )
  const isBankDraftSubscriber =
    user.business?.private_metadata?.plan_type === 'bank_draft_subscriber'
  const chargebee = subscription.chargebee
  const isSpinnerShown = subscription.loading && !chargebee

  useEffect(() => {
    if (
      !subscription.loading &&
      !subscription.loaded &&
      !isBankDraftSubscriber
    ) {
      dispatch(loadBusinessSubscription())
    }
    // Mount only: a failed load leaves loaded false, so re-running on change would retry forever
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isSpinnerShown || window.location.hash !== '#discovery-suite') return

    const discoverySuiteMarker = document.getElementById('discovery-suite')
    if (discoverySuiteMarker) {
      scrollIntoView(discoverySuiteMarker, {
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [isSpinnerShown])

  if (isSpinnerShown) return <Spinner />

  if (subscription.error?.body?.error === 'invalid_subscription_id') {
    return (
      <Callout.Root color="red">
        <Callout.Text>
          Sorry, we couldn’t find a subscription for this account.{' '}
          <ContactUsLink intercomMsg="My billing page is showing 'subscription not found'.">
            Please contact support.
          </ContactUsLink>
        </Callout.Text>
      </Callout.Root>
    )
  }

  const needsChargebeeScript =
    constance.SELF_SERVE_CANCELLATION_ROLLOUT &&
    constance['SUB-735-CHARGEBEE-CANCELLATION']
  const addOnCharges = chargebee?.addons ?? []
  const hasDiscoverySuite = hasAddOnCharge(addOnCharges, 'discovery_suite')
  // Quota can come from outside Chargebee (e.g. bank-draft subscribers), so an account can own Watermarking with no charge
  const hasWatermarking =
    hasAddOnCharge(addOnCharges, 'watermarking') || watermarkingQuota > 0
  const activeAddOns = getActiveAddOns({
    charges: addOnCharges,
    currencyCode: chargebee?.currency_code,
    locale,
    discoverySuiteQuota,
    discoverySuiteUsed,
    hasWatermarking,
    watermarkingQuota,
    watermarkingUsed,
    watermarkingUnlimited,
  })
  const addOnsTotal = getAddOnsTotal({
    charges: addOnCharges,
    period: period ?? null,
    currencyCode: chargebee?.currency_code,
    locale,
  })
  // Without the subscription we can't tell which add-ons are owned, as on the legacy page
  const hasAddOnOffers =
    canChangePlan &&
    !subscription.error &&
    (!hasDiscoverySuite || !hasWatermarking)

  // As on the legacy page, so admins can cancel even when the subscription fails or skips loading
  const canCancel = isAdmin && !chargebee?.scheduled_cancellation_at
  let addOnDisabledReason: string | undefined
  if (!isAdmin) {
    addOnDisabledReason = ADMIN_ONLY_MESSAGE
  } else if (pricingPage.hasFailed) {
    addOnDisabledReason = PRICING_PAGE_ERROR_MESSAGE
  }

  const card =
    chargebee && !isBankDraftSubscriber ? getCardDetails(chargebee) : null
  const billedTo = chargebee ? getBilledToLines(chargebee, locale) : []
  const hasManageBillingButton = card !== null || billedTo.length > 0

  return (
    <YourSubscription
      period={period ?? null}
      // TODO: STELLA-31 - with the translations, format with dayjs 'LL' (localizedFormat plugin) so dates follow each language's order
      renewsOn={chargebee?.expires_at ? formatDate(chargebee.expires_at) : null}
      expiresOn={
        chargebee?.scheduled_cancellation_at
          ? formatDate(chargebee.scheduled_cancellation_at)
          : null
      }
      amount={
        chargebee
          ? formatBillingAmount(
              chargebee.current_billing_amount_in_cents,
              chargebee.currency_code,
              locale,
            )
          : null
      }
      card={card}
      billedTo={billedTo}
      planSection={
        corePlan && (
          <PlanRow
            planName={corePlan.name}
            price={getPlanPrice(
              chargebee?.plan_charge,
              chargebee?.currency_code,
              locale,
            )}
            seats={{ used: usersLimits.current, allowance: usersLimits.max }}
            tracks={{
              used: tracksLimits.current,
              allowance: tracksLimits.max,
            }}
          />
        )
      }
      activeAddOns={activeAddOns}
      addOnsTotal={addOnsTotal}
      addOnOffers={
        hasAddOnOffers && (
          <>
            {!hasDiscoverySuite && (
              <Box>
                {/* Stays empty: the legacy #discovery-suite CSS shifts it up as a scroll offset */}
                <Box id="discovery-suite" />
                <SubscriptionAddOnCard
                  title="Discovery Suite"
                  description="Discovery suite lets you auto-tag tracks, share your whole catalog, search for similar tracks and generate instrumentals. Includes Catalogs, Similarity Search, Auto-tagging and Instant Instrumental. On the Pro plan or above, Discovery Suite unlocks Lyric Transcription."
                  learnMoreUrl={DISCOVERY_SUITE_LEARN_MORE_URL}
                  ctaHref={pricingPage.url}
                  ctaDisabledReason={addOnDisabledReason}
                />
              </Box>
            )}
            {!hasWatermarking && (
              <SubscriptionAddOnCard
                title="Watermarking"
                description={WATERMARKING_DESCRIPTION}
                learnMoreUrl={WATERMARKING_LEARN_MORE_URL}
                ctaHref={pricingPage.url}
                ctaDisabledReason={addOnDisabledReason}
              />
            )}
          </>
        )
      }
      manageSubscriptionUrl={pricingPage.url}
      isAdmin={isAdmin}
      manageSubscriptionDisabled={pricingPage.hasFailed}
      canChangePlan={canChangePlan}
      hasChargebeeScriptLoaded={hasChargebeeScriptLoaded}
      hasChargebeeScriptFailed={hasChargebeeScriptFailed}
      chargebeeScriptLoader={
        isAdmin &&
        (needsChargebeeScript || hasManageBillingButton) && (
          <ChargebeeScriptLoader
            onLoad={() => {
              setHasChargebeeScriptLoaded(true)
              setHasChargebeeScriptFailed(false)
            }}
            onError={error => {
              setHasChargebeeScriptLoaded(false)
              setHasChargebeeScriptFailed(true)
              captureError(error, {
                description: 'ChargebeeScriptLoader in YourSubscription',
              })
            }}
          />
        )
      }
      cancelSubscription={
        canCancel &&
        (isEnterprise ? (
          <EnterpriseCancelButton />
        ) : (
          <SubscribedUserBillingCancelButton
            size="2"
            cancelSubscriptionModalOpen={cancelSubscriptionModalOpen}
            setCancelSubscriptionModalOpen={setCancelSubscriptionModalOpen}
            // Only waits when the cancellation rollout needs the script; otherwise it may never load
            hasChargebeeScriptLoaded={
              !needsChargebeeScript || hasChargebeeScriptLoaded
            }
          />
        ))
      }
      loadError={
        subscription.error && (
          <Callout.Root color="red">
            <Callout.Text>
              We couldn’t load your subscription details. Refresh the page to
              try again, or{' '}
              <ContactUsLink intercomMsg="My billing page couldn't load my subscription details.">
                contact support
              </ContactUsLink>
              .
            </Callout.Text>
          </Callout.Root>
        )
      }
    />
  )
}

export default YourSubscriptionContainer
